import { z } from "zod";

import { filter } from "~/tools/filter";
import { fromJSON } from "~/tools/from-json";
import { isPresent } from "~/tools/is-present";
import { isString } from "~/tools/is-string";
import type { JSON } from "~/tools/json";

import { Api } from "../openapi";

import { dashboardMetricLabelNamesVariableSchema } from "./_dashboard-metric-label-names-variable";
import { dashboardMetricLabelValuesVariableSchema } from "./_dashboard-metric-label-values-variable";
import { dashboardMetricPromQLVariableSchema } from "./_dashboard-metric-promql-variable";
import { dashboardStaticListVariableSchema } from "./_dashboard-static-list-variable";
import type { DashboardVariableValue } from "./_dashboard-variable";

export const dashboardListVariableSortSchema = z.nativeEnum(Api.PersesVariableSort);

export type DashboardListVariableSort = z.infer<typeof dashboardListVariableSortSchema>;

const singleValuePersesSchema = z
	.object({
		kind: z.literal("singleValue"),
		singleValue: z.string().trim().catch(""),
	})
	.transform(({ singleValue }) => {
		const [, result] = fromJSON(singleValue);

		return result?.unsafe;
	});

const valueSchema = z.object({
	type: z.literal("Value"),
	value: z.string().trim().catch(""),
});

const everythingSchema = z.object({
	type: z.literal("Everything"),
});

const sliceValuePersesSchema = z
	.object({
		kind: z.literal("sliceValues"),
		sliceValues: z.array(z.string().trim().optional().catch(undefined)).transform(filter(isPresent)).catch([]),
	})
	.transform((input) => ({
		type: "Values" as const,
		values: input.sliceValues,
	}));

const dashboardListVariableRawSchema = z.object({
	allowMultiple: z.boolean().catch(false),

	allowAllValue: z.boolean().catch(false),
	customAllValue: z
		.string()
		.trim()
		.transform((input) => input || undefined)
		.catch(undefined),

	defaultValue: z
		.union([sliceValuePersesSchema, singleValuePersesSchema.pipe(z.union([everythingSchema, valueSchema]))])
		.optional()
		.catch(undefined),

	sort: dashboardListVariableSortSchema.optional().catch(undefined),

	plugin: z
		.union([
			z.object({
				kind: z.enum(["StaticList", "StaticListVariable"]).transform(() => "StaticList" as const),
				spec: dashboardStaticListVariableSchema,
			}),

			z.object({
				kind: z
					.enum(["MetricLabelNames", "MetricsNamesVariable", "PrometheusLabelNamesVariable"])
					.transform(() => "MetricLabelNames" as const),
				spec: dashboardMetricLabelNamesVariableSchema,
			}),

			z.object({
				kind: z
					.enum(["MetricLabelValues", "MetricsValuesVariable", "PrometheusLabelValuesVariable"])
					.transform(() => "MetricLabelValues" as const),
				spec: dashboardMetricLabelValuesVariableSchema,
			}),

			z.object({
				kind: z
					.enum(["MetricPromQL", "MetricPromQLVariable", "PrometheusPromQLVariable"])
					.transform(() => "MetricPromQL" as const),
				spec: dashboardMetricPromQLVariableSchema,
			}),
		])
		.catch({
			kind: "StaticList",
			spec: {
				values: [],
			},
		}),
});

export type DashboardListVariableRaw = z.infer<typeof dashboardListVariableRawSchema>;

export function narrowRawDashboardListVariable({
	allowMultiple,
	allowAllValue,
	customAllValue,
	defaultValue,
	...variable
}: DashboardListVariableRaw) {
	if (!defaultValue) {
		return {
			...variable,
			allowMultiple,
			allowAllValue,
			customAllValue,
			defaultValue,
		};
	}

	if (allowAllValue && defaultValue.type === "Everything") {
		return {
			...variable,
			allowMultiple,
			allowAllValue,
			customAllValue,
			defaultValue,
		};
	}

	if (allowMultiple) {
		const defaultValueWithFallback: Extract<typeof defaultValue, { type: "Values" }> =
			defaultValue.type === "Values" ? defaultValue : { type: "Values", values: [] };

		return {
			...variable,
			allowMultiple,
			allowAllValue,
			customAllValue: allowAllValue ? customAllValue : undefined,
			defaultValue: defaultValueWithFallback,
		};
	}

	const defaultValueWithFallback: Extract<typeof defaultValue, { type: "Value" }> =
		defaultValue.type === "Value" ? defaultValue : { type: "Value", value: "" };

	return {
		...variable,
		allowMultiple,
		allowAllValue,
		customAllValue: allowAllValue ? customAllValue : undefined,
		defaultValue: defaultValueWithFallback,
	};
}

export const dashboardListVariableSchema = dashboardListVariableRawSchema.transform(narrowRawDashboardListVariable);

export type DashboardListVariableValue = DashboardListVariable["defaultValue"];

export type DashboardListVariable = z.infer<typeof dashboardListVariableSchema>;

export type DashboardVariableSource = DashboardListVariable["plugin"]["kind"];

export function updateListVariableValue<TVariable extends DashboardListVariable>(
	value: DashboardVariableValue,
	variable: TVariable,
): TVariable {
	if (!value) {
		return variable;
	}

	if (isString(value)) {
		if (!variable.allowMultiple) {
			return {
				...variable,
				defaultValue: { type: "Value", value },
			};
		}

		return {
			...variable,
			defaultValue: { type: "Values", values: [value] },
		};
	}

	if (value.type === "Value") {
		if (!variable.allowMultiple) {
			return {
				...variable,
				defaultValue: value,
			};
		}

		return {
			...variable,
			defaultValue: { type: "Values", values: [value.value] },
		};
	}

	if (value.type === "Values") {
		if (variable.allowMultiple) {
			return { ...variable, defaultValue: value };
		}

		return {
			...variable,
			defaultValue: { type: "Value", value: value.values.at(0) ?? "" },
		};
	}

	/**
	 * value.type === "Everything"
	 */
	if (variable.allowAllValue) {
		return { ...variable, defaultValue: value };
	}

	/*
    Covers the last cases for allowAllValue being undefined:

    allowMultiple: true, allowAllValue: false, defaultValue: { type: "Values", ... }
    allowMultiple: false, allowAllValue: false, defaultValue: { type: "Value", ... }

    Both cases are already covered above, as allowAllValue has no bearing on whether or not
    "Value" or "Values" types can be applied
  */
	return variable;
}

export function listVariablePluginToPerses(plugin: DashboardListVariable["plugin"]): Api.PersesPlugin {
	if (plugin.kind === "StaticList") {
		return plugin satisfies JSON;
	}

	if (plugin.kind === "MetricPromQL") {
		return {
			...plugin,
			spec: {
				...plugin.spec,
				expr: plugin.spec.expr.source,
			},
		} satisfies JSON;
	}

	return {
		...plugin,
		spec: {
			...plugin.spec,
			matchers: plugin.spec.matchers.map((matcher) => matcher.source),
		},
	} satisfies JSON;
}
